import React, { useMemo } from 'react';
import qs from 'query-string';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons';

import UpgradeModal, { UpgradeProps } from '~/App/Components/Modals/Upgrade';
import useRollUpState from '~/App/Modules/TodoList/use-roll-up-state';
import { TodoCreateMode } from '~/App/Modules/TodoList/Modals/TodoCreate';
import { Greet } from '~/types/models/index';
import {
  openSendWithYourDomainEmailModal,
  SendWithYourDomainEmailModal,
} from '../Modals/SendWithYourDomainEmailModal';
import {
  trackUpgradeModalView,
  UpgradeModalViewContext,
} from '~/App/Services/BillingAnalytics';
import useCurrentUser from '~/react-hooks/use-current-user';
import { showSignupToUpgradeModal } from '~/App/Components/Modals/UpgradeModals/helpers';
import { useFeatures } from '~/react-hooks/use-features';
import { startUpgradeModalFlow } from '~/App/Components/Modals/UpgradeModalFlow';
import { UpgradeModalTrigger } from '~/App/Components/Modals/UpgradeModalFlow/util';

interface RollUpButtonProps {
  greets: Array<Greet>;
  disabled?: boolean;
}

const getTotalRecipients = (greets: Array<Greet>) => {
  return greets.reduce(
    (total: number, greet: Greet) => greet.profiles.length + total,
    0
  );
};

export default function RollUpButton({ greets, disabled }: RollUpButtonProps) {
  const { setRollUp } = useRollUpState();
  const { features, from_signature } = useCurrentUser();
  const { canSwitchPlan } = useFeatures();
  const plandalf = window.plandalf;

  const rollupBtnEnabled: boolean = greets.length > 1;
  const canRollUp: boolean = useMemo(
    () => features.canRollupGreets && greets.length > 1,
    [greets.length, features.canRollupGreets]
  );

  const rollUpLimit = features.greetRollUpLimit;
  const recipients: number = useMemo(
    () => getTotalRecipients(greets),
    [greets]
  );

  const upgradeModalProps: UpgradeProps = {
    referer: 'todo-rollups',
    redirect: '/home',
    onOpen() {
      trackUpgradeModalView(UpgradeModalViewContext.RollupUpsell);
    },
  };

  const shouldShowSendWithDomainEmailModal = useMemo(() => {
    const isSendingUsingDefault = from_signature?.is_default ?? true;
    if (isSendingUsingDefault && !features.canSendRollupsUsingDefaultEmail) {
      return true;
    }

    const isSendingUsingOAuth = from_signature?.is_integration ?? false;
    if (isSendingUsingOAuth && !features.canSendRollupsUsingOAuth) {
      return true;
    }

    return false;
  }, [from_signature, features]);

  const isOverRollUpLimit = rollUpLimit != null && recipients > rollUpLimit;
  const shouldShowUpgradeModal =
    !canRollUp || !rollupBtnEnabled || isOverRollUpLimit;

  function handleRollUpClick(evt: React.MouseEvent) {
    if (!canSwitchPlan) {
      showSignupToUpgradeModal();
      return;
    }

    if (shouldShowUpgradeModal) {
      const startRollupPaywall = 'start-rollup-paywall';

      if (
        plandalf.isWorkflowActive(startRollupPaywall) &&
        features.canPurchaseRollUpLimitAddOn
      ) {
        trackUpgradeModalView(UpgradeModalViewContext.NewRollupUpsell);

        plandalf.dispatch(startRollupPaywall);
      } else {
        startUpgradeModalFlow({
          trigger: UpgradeModalTrigger.RollupLimit,
          withAddOn: features.canPurchaseRollUpLimitAddOn,
          reason: 'Rollup limit reached',
        });
        evt.preventDefault();
      }
      return;
    }

    if (shouldShowSendWithDomainEmailModal) {
      openSendWithYourDomainEmailModal();
      evt.preventDefault();
      return;
    }

    setRollUp(greets);

    if (canRollUp) {
      window.location.href = `#/todo/create?${qs.stringify({
        mode: TodoCreateMode.ROLLUP,
      })}`;
    }
  }

  return (
    <React.Fragment>
      <Button
        variant={ButtonVariant.Primary}
        onClick={handleRollUpClick}
        className='h-10 py-0 text-base font-bold'
        disabled={!rollupBtnEnabled || disabled}
      >
        <FontAwesomeIcon icon={faRotateRight} />
        <span className='ml-3.5'>Roll-up</span>
      </Button>
      <SendWithYourDomainEmailModal />
      <UpgradeModal {...upgradeModalProps} />
    </React.Fragment>
  );
}
